import { Box, Button, Card, Grid, Icon, Paper } from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { TextValidator, ValidatorForm } from "react-material-ui-form-validator";
import { connect } from "react-redux";
import { NavLink, useParams, useRouteMatch } from "react-router-dom";
import AppBreadCrumb from "../../../../components/AppBreadCrumb/AppBreadCrumb";
import ConfirmationDialog from "../../../../utilities/components/ConfirmationDialog/ConfirmationDialog";
import { APP_URLS } from "../../../../utilities/constants";
import { PhoneInputValidator } from "../../../../utilities/validators";
import UtilHelper from "../../../../utilities/validators/UtilHelper";
import {
  addHeadOrgRequest,
  getHeadOrgByIdRequest,
  updateOrgRequest,
} from "../../HeadOrgsApiActions";
import { useStyles } from "./styles";

/**
 * function to render organization form
 * @param location: location from router
 * @param history: history from router
 * @param getOrgTypes: function to get organization types
 * @param addOrganisation: function to add organization
 * @param getOrgById: function to get organization by id
 * @returns {JSX.Element}
 * @constructor
 */
const HeadOrgForm = ({
  location,
  history,
  addHeadOrg,
  getHeadOrgById,
  updateHeadOrg,
}) => {
  const classes = useStyles();
  const params = useParams();
  const match = useRouteMatch();
  const currentUrlPath = match.path.split(":")[0];

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [ownerName, setOwnerName] = useState("");
  const [ownerContact, setOwnerContact] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [address, setAddress] = useState("");
  const [reAuthentication, setReAuthentication] = useState(null);
  const [reAuthenticationTime, setReAuthenticationTime] = useState("");
  const [requireLogin, setRequireLogin] = useState("");
  const [openCancelConfirmation, setOpenCancelConfirmation] = useState(false);

  useEffect(() => {
    ValidatorForm.addValidationRule("isBlank", (value) =>
      UtilHelper.validateBlankSpace(value)
    );
    ValidatorForm.addValidationRule("isValidMobile", (value) =>
      UtilHelper.validateMobile(value)
    );
    ValidatorForm.addValidationRule("isMaxLength", (value) =>
      UtilHelper.validateMaxLength(value)
    );

    return () => {
      ValidatorForm.removeValidationRule("isBlank");
      ValidatorForm.removeValidationRule("isValidMobile");
      ValidatorForm.removeValidationRule("isMaxLength");
    };
  }, []);

  useEffect(() => {
    if (params.id) {
      getHeadOrgById(params.id).then((res) => {
        setName(res.ownerName);
        setDescription(res.clinicDescription);
        setOwnerName(res.ownerName);
        setOwnerEmail(res.ownerEmail);
        setOwnerContact(res.ownerPhone);
        setAddress(res.ownerAddress);
        if (res?.requireLogin) {
          setRequireLogin(res.requireLogin.toString());
        }

        if (res.reauthenticationType) {
          setReAuthentication(res.reauthenticationType.toString());
        }
        setReAuthenticationTime(
          res.reauthenticationValue ? res.reauthenticationValue : null
        );
      });
    }
  }, [params.id, getHeadOrgById]);

  const breadCrumbDataForAddOrgForm = [
    {
      icon: "business",
      name: "Head Organizations",
      path: APP_URLS.HEADORGS,
    },
    {
      name: `Add Head Organizations`,
      path: APP_URLS.HEADORGS_ADD,
    },
  ];

  const breadCrumbDataForUpdateOrgForm = [
    {
      icon: "business",
      name: "Head Organizations",
      path: APP_URLS.ORGANIZATIONS,
    },
    {
      name: `Update Head Organizations`,
      path: `${APP_URLS.ORGANIZATIONS_EDIT}/${params.id}`,
    },
  ];

  /**
   * function to get breadcrumb data for current url
   */
  const getBreadcrumbData = (urlPath) => {
    switch (urlPath) {
      case APP_URLS.HEADORGS_ADD: {
        return breadCrumbDataForAddOrgForm;
      }
      case `${APP_URLS.HEADORGS_EDIT}/`: {
        return breadCrumbDataForUpdateOrgForm;
      }
      default: {
        return [];
      }
    }
  };

  /**
   * function to add org
   * @param e: event object on form submission
   */
  const onAdd = async (e) => {
    e.preventDefault();
    try {
      const data = {
        clinicDescription: description,
        ownerName,
        ownerEmail,
        ownerPhone: ownerContact,
        ownerAddress: address,
        reauthenticationType: reAuthentication,
        reauthenticationValue: reAuthenticationTime,
        requireLogin: requireLogin === "true",
      };

      const response = await addHeadOrg(data);

      history.push({
        pathname: `${APP_URLS.HEADORGS}/${response.id}`,
        state: { reload: Date.now() },
      });
    } catch (error) {
      console.error("Error while adding organisation:", error);
    }
  };

  /**
   * function to update org
   * @param e: event object on form submission
   */
  const onUpdate = async (e) => {
    e.preventDefault();

    try {
      const data = {
        clinicDescription: description,
        ownerName,
        ownerEmail,
        ownerPhone: ownerContact,
        ownerAddress: address,
        reauthenticationType: reAuthentication,
        reauthenticationValue: reAuthenticationTime,
        requireLogin: requireLogin === "true",
      };

      await updateHeadOrg(params.id, data);
      history.goBack();
    } catch (error) {
      console.error("Error updating organization:", error);
    }
  };

  /**
   * function to handle cancel add or update
   */
  const handleCancel = () => {
    history.goBack();
  };

  return (
    <Box className={classes.outerContainer}>
      <AppBreadCrumb>
        {getBreadcrumbData(currentUrlPath).map((crumb, index) => (
          <Box
            key={index}
            component={NavLink}
            exact
            to={{
              pathname: crumb.path,
              state: {
                orgIdHistory: location.state && location.state.orgIdHistory,
                orgNameHistory: location.state && location.state.orgNameHistory,
              },
            }}
            className={classes.breadcrumbItem}
            activeClassName={classes.activeBreadcrumbItem}
          >
            {crumb.icon && (
              <Icon className={classes.breadcrumpIcon}>{crumb.icon}</Icon>
            )}
            <Box className={classes.breadcrumpLabel}>{crumb.name}</Box>
          </Box>
        ))}
      </AppBreadCrumb>

      <Box mt={2}>
        <ValidatorForm onSubmit={params.id ? onUpdate : onAdd}>
          <Box className={classes.addOragnizationContainer}>
            <Box className={classes.addOragnizationFormContainer}>
              <Box
                component={Paper}
                className={classes.addOragnizationFormInnerContainer}
              >
                <Grid container>
                  <Grid item xs={12} sm={12} md={8} lg={8} xl={8}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                        <TextValidator
                          label="Organization Name *"
                          variant="outlined"
                          fullWidth
                          size="small"
                          value={ownerName}
                          onChange={(e) => setOwnerName(e.target.value)}
                          validators={["required", "isBlank", "isMaxLength"]}
                          errorMessages={[
                            "Organization name is required",
                            "Please enter Organization name",
                            "Maximum 100 letters are accepted.",
                          ]}
                        />
                      </Grid>
                      <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                        <PhoneInputValidator
                          autoFormat={false}
                          variant="outlined"
                          size="small"
                          label="Organization Contact *"
                          fullWidth
                          defaultCountry="in"
                          countryCodeEditable={false}
                          value={ownerContact}
                          onChange={(phone) => {
                            let phoneNumber =
                              "+" + phone.replace(/[^0-9]+/g, "");
                            setOwnerContact(phoneNumber);
                          }}
                          validators={["required", "isValidMobile"]}
                          errorMessages={[
                            "Organization Contact is required",
                            "Please enter valid phone",
                          ]}
                        />
                      </Grid>
                      <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                        <TextValidator
                          label="Organization Email *"
                          variant="outlined"
                          fullWidth
                          size="small"
                          value={ownerEmail}
                          onChange={(e) => setOwnerEmail(e.target.value)}
                          validators={["required", "isEmail"]}
                          errorMessages={[
                            "Organization Email is required",
                            "Please enter valid email",
                          ]}
                        />
                      </Grid>
                      <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                        <TextValidator
                          label="Organization Description *"
                          variant="outlined"
                          fullWidth
                          size="small"
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          validators={["required", "isBlank", "isMaxLength"]}
                          errorMessages={[
                            "Organization Description is required",
                            "Please enter Organization description",
                            "Maximum 100 letters are accepted.",
                          ]}
                        />
                      </Grid>
                      <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                        <TextValidator
                          label="Address *"
                          variant="outlined"
                          fullWidth
                          size="small"
                          value={address}
                          onChange={(e) => setAddress(e.target.value)}
                          validators={["required", "isBlank", "isMaxLength"]}
                          errorMessages={[
                            "Address is required",
                            "Please enter Address",
                            "Maximum 100 letters are accepted.",
                          ]}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </Box>

          <Box className={classes.footerContainer}>
            <Box
              component={Card}
              raised
              className={classes.footerContainerInner}
            >
              <Box>
                <Button
                  variant="outlined"
                  color="primary"
                  disableElevation={true}
                  onClick={() => setOpenCancelConfirmation(true)}
                >
                  Cancel
                </Button>

                <Button
                  variant="contained"
                  color="primary"
                  disableElevation={true}
                  type="submit"
                  className={classes.saveButton}
                >
                  Save
                </Button>
              </Box>
            </Box>
          </Box>
        </ValidatorForm>
      </Box>

      <Box>
        <ConfirmationDialog
          open={openCancelConfirmation}
          setOpen={setOpenCancelConfirmation}
          title="Cancel"
          message="Are you sure you want to cancel?"
          onConfirm={handleCancel}
        />
      </Box>
    </Box>
  );
};

const mapStateToProps = (state) => {
  return {};
};

const mapDispatchToProps = (dispatch) => {
  return {
    addHeadOrg: (data) => {
      return dispatch(addHeadOrgRequest(data)).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
    getHeadOrgById: (id) => {
      return dispatch(getHeadOrgByIdRequest(id)).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
    updateHeadOrg: (id, data) => {
      return dispatch(updateOrgRequest(id, data)).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(HeadOrgForm);
